import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Phone, Mail, MapPin, ArrowRight, Home, Wrench } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Breadcrumb from '../components/Breadcrumb';
import Footer from '../components/Footer';
import { siteConfig } from '../data/mock';

const breadcrumbItems = [
  { label: 'Home', href: '/' },
  { label: 'Contact', href: '/contact', current: true }
];

const ContactPage = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#1F3B60' }}>
      <Helmet>
        <title>Contact Us | Pacific Crest Properties — Los Angeles</title>
        <meta name="description" content="Contact Pacific Crest Properties for real estate solutions in Los Angeles. Call (310) 596-6534 or email office@pacificcrestprop.com." />
        <meta name="keywords" content="contact Pacific Crest Properties, Los Angeles real estate contact, property solutions LA contact" />
        <link rel="canonical" href="https://pacificcrestprop.com/contact" />
        <meta property="og:title" content="Contact | Pacific Crest Properties" />
        <meta property="og:description" content="Get in touch with Pacific Crest Properties for real estate solutions in Los Angeles." />
        <meta property="og:url" content="https://pacificcrestprop.com/contact" />
      </Helmet>
      
      <Header />
      <div className="pt-20">
        <Breadcrumb items={breadcrumbItems} variant="dark" />
      </div>
      
      {/* Hero Section */}
      <section className="pt-8 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-block font-semibold text-xs tracking-[0.2em] uppercase mb-4 px-4 py-1.5 rounded-full" style={{ color: '#D6A94F', backgroundColor: 'rgba(214, 169, 79, 0.1)', border: '1px solid rgba(214, 169, 79, 0.2)' }}>
            Get In Touch
          </span>
          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-white mb-6">
            Contact Us
          </h1>
          <div className="divider-gold mx-auto mb-8" />
          <p className="text-xl text-white/70 leading-relaxed max-w-3xl mx-auto">
            Ready to discuss your property goals? Reach out to us today.
          </p>
        </div>
      </section>

      {/* Contact Info Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#F4F1E9' }}>
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Details */}
            <div>
              <h2 className="font-display font-bold text-2xl mb-8" style={{ color: '#1F3B60' }}>
                Contact Information
              </h2>

              <div className="space-y-6">
                <a
                  href={`tel:${siteConfig.phone.replace(/[^0-9]/g, '')}`}
                  className="flex items-center space-x-4 group"
                >
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300" style={{ background: 'linear-gradient(135deg, #D6A94F, #E4C078)' }}>
                    <Phone className="w-6 h-6" style={{ color: '#1F3B60' }} />
                  </div>
                  <div>
                    <span className="block text-sm text-gray-500 mb-1">Phone</span>
                    <span className="text-lg font-semibold" style={{ color: '#1F3B60' }}>
                      {siteConfig.phone}
                    </span>
                  </div>
                </a>

                <a
                  href={`mailto:${siteConfig.email}`}
                  className="flex items-center space-x-4 group"
                >
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300" style={{ background: 'linear-gradient(135deg, #D6A94F, #E4C078)' }}>
                    <Mail className="w-6 h-6" style={{ color: '#1F3B60' }} />
                  </div>
                  <div>
                    <span className="block text-sm text-gray-500 mb-1">Email</span>
                    <span className="text-lg font-semibold" style={{ color: '#1F3B60' }}>
                      {siteConfig.email}
                    </span>
                  </div>
                </a>

                <div className="flex items-start space-x-4">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0" style={{ background: 'linear-gradient(135deg, #D6A94F, #E4C078)' }}>
                    <MapPin className="w-6 h-6" style={{ color: '#1F3B60' }} />
                  </div>
                  <div>
                    <span className="block text-sm text-gray-500 mb-1">Location</span>
                    <span className="text-lg font-semibold block" style={{ color: '#1F3B60' }}>
                      {siteConfig.address.street}
                    </span>
                    <span className="text-gray-600">
                      {siteConfig.address.city}, {siteConfig.address.state} {siteConfig.address.zip}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA Card */}
            <div
              className="p-8 rounded-3xl relative overflow-hidden"
              style={{ backgroundColor: '#1F3B60' }}
            >
              <div
                className="absolute top-0 right-0 w-64 h-64 rounded-full blur-3xl"
                style={{ background: 'rgba(214, 169, 79, 0.15)' }}
              />

              <div className="relative z-10">
                <h3 className="font-display font-bold text-2xl text-white mb-4">
                  Ready to Get Started?
                </h3>
                <p className="text-white/70 mb-8 leading-relaxed">
                  Whether you&apos;re looking to sell your property or explore renovation options, we&apos;re here to help.
                </p>

                <div className="space-y-4">
                  <Link
                    to={siteConfig.links.offers}
                    className="btn-gold w-full flex items-center justify-center space-x-2 group"
                  >
                    <Home className="w-5 h-5" />
                    <span>Get Your Offer</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                  <Link
                    to={siteConfig.links.renovations}
                    className="btn-outline-gold w-full flex items-center justify-center space-x-2 group"
                  >
                    <Wrench className="w-5 h-5" />
                    <span>Request Renovation Quote</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Back to Home */}
      <section className="py-12 px-4 text-center" style={{ backgroundColor: '#1F3B60' }}>
        <Link
          to="/"
          className="inline-flex items-center space-x-2 font-medium text-white/60 hover:text-white transition-colors duration-300"
        >
          <ArrowRight className="w-4 h-4 rotate-180" />
          <span>Back to Home</span>
        </Link>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
